/**
 * CBC Metrics Display Component
 *
 * Displays Conversational Boundary Control metrics for simulation runs.
 * Shows effectiveness score, therapeutic ratio, drift episodes, and other CBC metrics.
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Security as CBCIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  TrendingDown as DriftIcon,
  Psychology as TherapyIcon
} from '@mui/icons-material';

/**
 * Get color based on effectiveness score
 */
const getEffectivenessColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
};

/**
 * Get color for therapeutic ratio
 */
const getTherapeuticRatioColor = (ratio) => {
  if (ratio >= 0.7) return 'success.main';
  if (ratio >= 0.5) return 'warning.main';
  return 'error.main';
};

/**
 * CBC Metrics Display Component
 * @param {Object} props
 * @param {Object} props.cbcMetrics - CBC metrics object from simulation run
 * @param {Object} props.cbcConfig - CBC configuration used for the run
 * @param {boolean} props.compact - Whether to show compact view
 */
function CBCMetricsDisplay({ cbcMetrics, cbcConfig, compact = false }) {
  // If CBC wasn't enabled or no metrics, show disabled state
  if (!cbcConfig?.enabled || !cbcMetrics) {
    return (
      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
        <CardContent sx={{ py: compact ? 1.5 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CBCIcon color="disabled" fontSize={compact ? 'small' : 'medium'} />
            <Typography variant={compact ? 'body2' : 'body1'} color="text.secondary">
              CBC Disabled
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const {
    cbcEffectivenessScore = 0,
    therapeuticContentRatio = 0,
    driftEpisodeCount = 0,
    maxDriftScore = 0,
    avgDriftScore = 0,
    resistanceCount = 0,
    roleAbandonmentDetected = false,
    reconnectionAttempts = 0,
    reconnectionSuccessRate = 0,
    nonTherapeuticExchangeCount = 0
  } = cbcMetrics;

  const effectivenessColor = getEffectivenessColor(cbcEffectivenessScore);

  // Compact view for list displays
  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="CBC Effectiveness Score">
          <Chip
            icon={<CBCIcon />}
            label={`${cbcEffectivenessScore}/100`}
            size="small"
            color={effectivenessColor}
          />
        </Tooltip>
        <Tooltip title="Therapeutic Content Ratio">
          <Chip
            label={`${Math.round(therapeuticContentRatio * 100)}% Therapeutic`}
            size="small"
            variant="outlined"
          />
        </Tooltip>
        {driftEpisodeCount > 0 && (
          <Tooltip title="Drift Episodes">
            <Chip
              icon={<DriftIcon />}
              label={`${driftEpisodeCount} drifts`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Tooltip>
        )}
        {roleAbandonmentDetected && (
          <Tooltip title="Role Abandonment Detected">
            <Chip
              icon={<WarningIcon />}
              label="Role Abandoned"
              size="small"
              color="error"
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  // Full view with detailed metrics
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CBCIcon color="primary" />
          <Typography variant="h6">
            CBC Metrics
          </Typography>
          <Chip
            label={cbcConfig.preset || 'Custom'}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Box>

        <Grid container spacing={2}>
          {/* Effectiveness Score */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h3" color={`${effectivenessColor}.main`}>
                {cbcEffectivenessScore}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Effectiveness Score
              </Typography>
              <LinearProgress
                variant="determinate"
                value={cbcEffectivenessScore}
                color={effectivenessColor}
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>

          {/* Therapeutic Ratio */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h3" sx={{ color: getTherapeuticRatioColor(therapeuticContentRatio) }}>
                {Math.round(therapeuticContentRatio * 100)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Therapeutic Content
              </Typography>
              <LinearProgress
                variant="determinate"
                value={therapeuticContentRatio * 100}
                color={therapeuticContentRatio >= 0.7 ? 'success' : therapeuticContentRatio >= 0.5 ? 'warning' : 'error'}
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>

          {/* Drift Episodes */}
          <Grid item xs={6} sm={3} md={1.5}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h4" color={driftEpisodeCount > 2 ? 'error.main' : 'text.primary'}>
                {driftEpisodeCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Drift Episodes
              </Typography>
            </Box>
          </Grid>

          {/* Resistance Count */}
          <Grid item xs={6} sm={3} md={1.5}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h4" color={resistanceCount > 5 ? 'warning.main' : 'text.primary'}>
                {resistanceCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Resistance Events
              </Typography>
            </Box>
          </Grid>

          {/* Reconnection Attempts */}
          <Grid item xs={6} sm={3} md={1.5}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h4">
                {reconnectionAttempts}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Reconnections
              </Typography>
            </Box>
          </Grid>

          {/* Reconnection Success */}
          <Grid item xs={6} sm={3} md={1.5}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h4" color={reconnectionSuccessRate >= 0.7 ? 'success.main' : 'warning.main'}>
                {Math.round(reconnectionSuccessRate * 100)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Success Rate
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Alert Indicators */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {roleAbandonmentDetected && (
            <Chip
              icon={<WarningIcon />}
              label="Role Abandonment Detected"
              color="error"
              size="small"
            />
          )}
          {nonTherapeuticExchangeCount > 5 && (
            <Chip
              icon={<DriftIcon />}
              label={`${nonTherapeuticExchangeCount} Non-Therapeutic Exchanges`}
              color="warning"
              size="small"
            />
          )}
          {cbcEffectivenessScore >= 80 && !roleAbandonmentDetected && (
            <Chip
              icon={<SuccessIcon />}
              label="Strong Boundary Maintenance"
              color="success"
              size="small"
            />
          )}
        </Box>

        {/* Detailed Metrics Table */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Detailed Metrics
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Max Drift Score:</Typography>
              <Typography variant="body2">{maxDriftScore.toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Avg Drift Score:</Typography>
              <Typography variant="body2">{avgDriftScore.toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Transparency Mode:</Typography>
              <Typography variant="body2">{cbcConfig.transparencyMode || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Non-Therapeutic:</Typography>
              <Typography variant="body2">{nonTherapeuticExchangeCount} exchanges</Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}

export default CBCMetricsDisplay;
